// src/pages/AccountActivation.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Card, CardContent, TextField, Button,
  InputAdornment, IconButton, LinearProgress
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Swal from "sweetalert2";
import { domain } from "../auth/domain";


const brand = "#800b33";
const brandHover = "#6c0a2b"; // darker shade for hover states

const AccountActivation = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      setError("Your session has expired. Please login again before setting a new password.");
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        setError("Your session has expired. Please login again before setting a new password.");
        navigate("/login", { replace: true });
        return;
      }
      const response = await fetch(`${domain}custom-password-reset/set_password/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${token}`,
        },
        body: JSON.stringify({
          new_password: newPassword,
          current_password: currentPassword,
        }),
      });

      if (response.ok) {
        await Swal.fire({
          icon: "success",
          title: "Account Activated!",
          text: "Your password has been updated. Please log in again.",
          confirmButtonColor: brand,
        });
        // Log the user out and redirect to login
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        navigate("/");
      } else {
        const errData = await response.json();
        if (response.status === 401) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          setError("Your session has expired. Please login again and retry password activation.");
          navigate("/login", { replace: true });
          return;
        }
        const errorMessage = errData.current_password?.[0] || errData.detail || "An unknown error occurred.";
        setError(errorMessage);
      }
    } catch (err) {
      setError("A network error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      }}
    >
      <Card sx={{ p: 4, borderRadius: 4, boxShadow: 6, maxWidth: 450, width: "100%" }}>
        <CardContent>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <LockResetIcon sx={{ fontSize: 48, color: brand }} />
            <Typography variant="h5" sx={{ fontWeight: 900, mt: 1 }}>
              Activate Your Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              For your security, please set a new password.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              label="Temporary Password"
              type="password"
              fullWidth
              margin="normal"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <TextField
              label="New Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />

            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, py: 1.5, bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
            >
              {loading ? <LinearProgress sx={{ width: '100%' }} /> : "Set New Password & Activate"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AccountActivation;
